import { useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";

import { getMsalConfig } from "../configuration.js";
import { loginSuccessEventCallback } from "../loginSuccess.js";
import { forgotPasswordEventCallback } from "../forgotPassword.js";
import BetweenPages from "./BetweenPages";
import useConfig from "../hooks/useConfig";

/**
 * Sets up the object used to perform authentication and authorization functions and handles active account assignment.
 * @returns {IPublicClientApplication} Client Application Interface
 */
function setupMsalInstance() {
	const msalInstance = new PublicClientApplication(getMsalConfig());

	msalInstance.addEventCallback((event) => {
		console.debug(`MSAL Event: ${event.eventType}`, event);

		const successEvents = [
			EventType.LOGIN_SUCCESS,
			EventType.ACQUIRE_TOKEN_SUCCESS,
			EventType.SSO_SILENT_SUCCESS,
		];

		if (successEvents.includes(event.eventType)) {
			const account = event.payload?.account;
			if (account) {
				msalInstance.setActiveAccount(account);
			} else {
				const allAccounts = msalInstance.getAllAccounts();
				if (allAccounts.length > 0) {
					msalInstance.setActiveAccount(allAccounts[0]);
				}
			}
		}

		if (event.eventType === EventType.LOGIN_FAILURE) {
			forgotPasswordEventCallback(event, msalInstance);
		}
	});

	return msalInstance;
}

/**
 * Resolves the redirect flow after an MSAL loginRedirect().
 * This ensures that the user is fully authenticated before the application renders.
 * If a login response is present, sets the active account and triggers post-login processing.
 * This avoids race conditions by centralizing the loginSuccessEventCallback call here
 */
async function initializeMsalRedirectFlow(msalInstance) {
	try {
		const response = await msalInstance.handleRedirectPromise();
		if (response?.account) {
			msalInstance.setActiveAccount(response.account);
			await loginSuccessEventCallback(msalInstance);
		} else {
			const accounts = msalInstance.getAllAccounts();
			if (accounts.length > 0) {
				msalInstance.setActiveAccount(accounts[0]);
			}
		}
	} catch (err) {
		console.error("MSAL redirect handling failed:", err);
	}
}

const loadHeap = (heapId) => {
	if (!heapId) {
		console.warn("Heap ID is not available.");
		return;
	}
	window.heapReadyCb = window.heapReadyCb || [];
	window.heap = window.heap || [];

	window.heap.load = function (e, t) {
		window.heap.envId = e;
		window.heap.clientConfig = t || {};
		window.heap.clientConfig.shouldFetchServerConfig = false;

		const script = document.createElement("script");
		script.type = "text/javascript";
		script.async = true;
		script.src = `https://cdn.us.heap-api.com/config/${e}/heap_config.js`;

		const firstScript = document.getElementsByTagName("script")[0];
		firstScript.parentNode.insertBefore(script, firstScript);

		const methods = ["init", "startTracking", "stopTracking", "track", "resetIdentity", "identify", "getSessionId", "getUserId", "getIdentity", "addUserProperties",
			"addEventProperties", "removeEventProperty", "clearEventProperties", "addAccountProperties", "addAdapter", "addTransformer", "addTransformerFn", "onReady",
			"addPageviewProperties", "removePageviewProperty", "clearPageviewProperties", "trackPageview"];

		const createMethod = (method) => (...args) => {
			window.heapReadyCb.push({
				name: method,
				fn: () => window.heap[method] && window.heap[method](...args),
			});
		};
		methods.forEach((method) => {
			window.heap[method] = createMethod(method);
		});
	};
	window.heap.load(heapId);
}

/**
 * This component utilises the useConfig hook, creates the PublicClientApplication instance, and wraps the MsalProvider component.
 * The PublicClientApplication instance is added as a prop to Azure's MsalProvider context provider component, which must be rendered above 
 * any other components that use MSAL.
 * @component
 */
const DynamicConfigMsalProvider = (props) => {
	const [config] = useConfig();
	const [msalInstance, setMsalInstance] = useState(null);

	useEffect(() => {
		if (!config?.length) {
			return;
		}

		window.env = {
			clientId: config[0],
			instance: config[1],
			domain: config[2],
			appIdUri: config[3],
			heapId: config[4],
			signalRHubUrl: config[5]
		};
		loadHeap(window.env.heapId);

		const instance = setupMsalInstance();

		initializeMsalRedirectFlow(instance).then(() => {
			setMsalInstance(instance);
		});
	}, [config]);

	if (!config?.length || !msalInstance) {
		return <BetweenPages />;
	}

	return <MsalProvider instance={msalInstance}>
		{props.children}
	</MsalProvider>;
};

export default DynamicConfigMsalProvider;